import React, { useState, useEffect } from 'react';
import BackgroundImage from './BackgroundImage';
import ParticlesBackground from './ParticlesBackground';

interface ZoneLoadingScreenProps {
  fromZone?: string;
  toZone?: string;
  progress?: number;
  loadingText?: string;
}

const ZoneLoadingScreen: React.FC<ZoneLoadingScreenProps> = ({ 
  fromZone = '',
  toZone = '',
  progress,
  loadingText: externalLoadingText
}) => {
  const [currentProgress, setCurrentProgress] = useState(0);
  const [loadingText, setLoadingText] = useState(externalLoadingText || 'Chargement...');
  const [tips] = useState([
    'Astuce : Utilisez les raccourcis clavier pour naviguer plus rapidement',
    'Astuce : Vérifiez régulièrement vos missions dans le journal',
    'Astuce : Les ressources se régénèrent automatiquement',
    'Astuce : Rejoignez une équipe pour des missions coopératives',
    'Astuce : Consultez la carte pour explorer le monde',
  ]);
  const [currentTip, setCurrentTip] = useState(0);

  // Animation de progression
  useEffect(() => {
    if (progress !== undefined && progress !== null) {
      // Utiliser directement la progression passée en props
      setCurrentProgress(Math.min(Math.max(progress, 0), 100));
    } else {
      // Simulation de progression si aucune progression n'est fournie
      const interval = setInterval(() => {
        setCurrentProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + Math.random() * 15;
        });
      }, 200);
      return () => clearInterval(interval);
    }
  }, [progress]);

  // Changement de texte de chargement (seulement si aucun texte externe n'est fourni)
  useEffect(() => {
    if (externalLoadingText) {
      setLoadingText(externalLoadingText);
      return;
    }
    
    const texts = [
      'Chargement...',
      'Synchronisation des données...',
      'Initialisation des ressources...',
      'Chargement des données...',
      'Finalisation...',
    ];
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % texts.length;
      setLoadingText(texts[index]);
    }, 1500);
    return () => clearInterval(interval);
  }, [externalLoadingText]);

  // Rotation des astuces
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip(prev => (prev + 1) % tips.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [tips.length]);

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center overflow-hidden">
      {/* Animation de fond comme Home */}
      <div className="absolute inset-0 z-0">
        <BackgroundImage />
        <ParticlesBackground />
      </div>
      
      {/* Overlay sombre pour la lisibilité */}
      <div className="absolute inset-0 bg-black/70 z-10" />

      {/* Effet de lignes animées */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-pulse" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-pulse" />
      </div>

      <div className="relative z-20 w-full max-w-2xl px-6">
        {/* Transition */}
        {(fromZone || toZone) && (
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-6">
              {fromZone && <div className="text-gray-400 text-sm font-medium">{fromZone}</div>}
              {(fromZone && toZone) && (
                <div className="flex items-center gap-2">
                  <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
                  <svg 
                    className="w-6 h-6 text-blue-400 animate-pulse" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
                </div>
              )}
              {toZone && <div className="text-blue-400 text-sm font-semibold">{toZone}</div>}
            </div>
          </div>
        )}

        {/* Logo/Titre */}
        <div className="text-center mb-8">
          <h1 className="text-5xl md:text-6xl font-bold mb-2 bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-pulse">
            OpSecours
          </h1>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent mx-auto" />
        </div>

        {/* Message de chargement */}
        <div className="mb-8 text-center">
          <p className="text-xl text-gray-300 mb-6 font-medium">
            {loadingText}
          </p>

          {/* Barre de progression principale */}
          <div className="relative mb-4">
            <div className="w-full h-4 bg-gray-800/50 rounded-full overflow-hidden border-2 border-gray-700 shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 rounded-full transition-all duration-200 ease-out relative overflow-hidden"
                style={{ width: `${Math.min(Math.max(currentProgress, 0), 100)}%` }}
              >
                {/* Effet de brillance animé */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
                {/* Particules sur la barre */}
                <div className="absolute inset-0">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute top-0 w-1 h-full bg-white/60 animate-pulse"
                      style={{
                        left: `${20 + i * 15}%`,
                        animationDelay: `${i * 0.3}s`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            {/* Pourcentage */}
            <div className="absolute -top-8 right-0 text-blue-400 font-bold text-lg tabular-nums">
              {Math.round(Math.min(Math.max(currentProgress, 0), 100))}%
            </div>
          </div>

          {/* Barre de progression secondaire (détails) */}
          <div className="w-full h-1 bg-gray-800/30 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500/50 to-blue-500/50 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(currentProgress * 1.2, 100)}%` }}
            />
          </div>
        </div>

        {/* Indicateur de chargement rotatif */}
        <div className="flex justify-center mb-8">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full" />
            <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin" />
            <div className="absolute inset-2 border-4 border-cyan-500/30 rounded-full" />
            <div className="absolute inset-2 border-4 border-transparent border-t-cyan-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
          </div>
        </div>

        {/* Astuce */}
        <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-4 backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-yellow-400 text-sm font-semibold mb-1">Astuce</p>
              <p className="text-gray-300 text-sm">{tips[currentTip]}</p>
            </div>
          </div>
        </div>

        {/* Texte en bas */}
        <p className="text-center text-xs text-gray-500 mt-6">
          Veuillez patienter pendant le chargement...
        </p>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.4; }
          50% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default ZoneLoadingScreen;

